import { all, extend } from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { Campaign, AdGroup, ResponsiveAd } from '@bingads-webui/campaign-odata-schemas';
import { ImageOData } from '@bingads-webui-campaign/image-odata';
import { ResponsiveAdRepository } from '@bingads-webui-campaign/responsive-ad-editor';
import { convertFromBulkResponse } from '@bingads-webui-campaign-react/responsive-ad-editor-utils';
import CampaignCacheManager from '@bingads-webui-campaign-legacy/cache-manager';
import config from '@bingads-webui-component-legacy/config/index';
import { isBulkWrappedResponse } from '../shared/bulk';
import { ERROR_ENTITY, getBulkUploadErrorType, modelToViewModelForBulkErrors, removeReferencedEntityInvalidIdErrors } from '../shared/bulk-upsert-errors';
import { GENERIC_ERROR_MESSAGE_KEY } from '../shared/constants';
import { viewModelToModelForMMA } from './multimedia-ad';

const bulkUpsertAdEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
  [getBulkUploadErrorType(ResponsiveAd)]: ERROR_ENTITY.ResponsiveAd,
};

const adResource = new (edmCampaign.types.resolve('Model.ResponsiveAd')).ResourceIdentifier();

export const deleteAd = (adId, options) => {
  const destroyCall = adResource.bulkDestroy.bind(adResource);
  return destroyCall(adId, options);
};

export const saveAd = ({
  ad: adViewModel,
  campaignId,
  adGroupId,
  cid,
  aid,
}, {
  i18n,
  scenario,
  activityLogger,
}) => {
  const adModel = viewModelToModelForMMA(adViewModel, campaignId, adGroupId);

  const responsiveAdRepository = new ResponsiveAdRepository({
    viewContext: {
      currentCampaign: {
        Id: campaignId,
      },
      currentAdGroup: {
        Id: adGroupId,
      },
    },
    isVideo: false,
    campaignDomain: '',
  });
  const oDataConfig = {
    odataRootUrl: config.get('CampaignAzureDomain'),
    odataToken: config.get('odata.token'),
    odataPath: config.get('odata.url') + config.get('odata.path'),
  };
  const imageOData = new ImageOData(cid, aid, oDataConfig);

  return responsiveAdRepository.processImageAndSaveSearchAdAsync(adModel, imageOData, false)
    .then(response => ({ data: convertFromBulkResponse(response) }))
    .catch((bulkOrSystemError) => {
      const { entity, errors } = bulkOrSystemError;

      if (entity && entity.Id) {
        extend(adViewModel, { Id: entity.Id });

        // Ignore errors if the entity was eventually created and all the errors are editorial errors
        if (all(errors, error => error.Error === 'CampaignServiceEditorialValidationError')) {
          CampaignCacheManager.invalidate('Ad'); // Cache is not automatically invalidated since the API failed
          return {
            data: adViewModel,
            editorialErrors: errors,
          };
        }
      }

      if (isBulkWrappedResponse(bulkOrSystemError)) {
        let filteredErrors = errors;
        if (entity.Id <= 0) {
          filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
        }

        const serverErrors = modelToViewModelForBulkErrors(
          filteredErrors,
          bulkUpsertAdEntityErrorMap,
          scenario,
          i18n,
          activityLogger
        );

        // eslint-disable-next-line prefer-promise-reject-errors
        return {
          data: adViewModel,
          serverErrors,
        };
      }

      // Activity
      // eslint-disable-next-line no-console
      console.warn(`Campaign bulk upsert system error: ${bulkOrSystemError.stack || bulkOrSystemError.message || bulkOrSystemError}`);
      // eslint-disable-next-line prefer-promise-reject-errors
      return {
        data: adViewModel,
        systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
      };
    });
};
